import { defineVariants } from "@/runtime/style-engine/define-variants";
import {
  dropdownRootConfig,
  dropdownContentConfig,
  dropdownGroupConfig,
} from "@/runtime/style-engine/shared-dropdown-styles";

/**
 * Tailwind-like class variants for `<e-combobox>`.
 */
export const comboboxClasses = defineVariants(dropdownRootConfig);

/**
 * Tailwind-like class variants for `<e-combobox-input>`.
 */
export const comboboxInputClasses = defineVariants({
  base: `
    inline-flex items-center gap-2
    border
    cursor-text
    outline-none
    transition-colors
    min-w-[160px]
  `,
  input: `
    flex-1
    bg-transparent
    border-none
    outline-none
    min-w-0
    text-inherit
  `,
  icon: `flex-shrink-0 text-inherit`,
  trigger: `flex-shrink-0 text-inherit cursor-pointer transition-transform`,

  variants: {
    color: {
      primary: `[--combobox-color:var(--color-primary)] focus-within:ring-white`,
      secondary: `[--combobox-color:var(--color-secondary)] focus-within:ring-white`,
      accent: `[--combobox-color:var(--color-accent)] focus-within:ring-white`,
      error: `[--combobox-color:var(--color-error)] focus-within:ring-white`,
      warning: `[--combobox-color:var(--color-warning)] focus-within:ring-white`,
      info: `[--combobox-color:var(--color-info)] focus-within:ring-white`,
      success: `[--combobox-color:var(--color-success)] focus-within:ring-white`,
      neutral: `[--combobox-color:var(--color-neutral)] focus-within:ring-white`,
      mono: `[--combobox-color:var(--color-foreground)] focus-within:ring-white`,
    },

    variant: {
      solid: `
        bg-(--combobox-color)/10 text-(--combobox-color)
        border-(--combobox-color)
        hover:bg-(--combobox-color)/15
      `,
      outline: `
        bg-transparent text-(--color-foreground)
        border-(--combobox-color)
        hover:bg-(--combobox-color)/5
      `,
      soft: `
        relative isolate backdrop-blur-lg
        bg-(--combobox-color)/10 text-(--color-foreground) border-transparent
        hover:bg-(--combobox-color)/15

        before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-(--color-background)/50
        after:absolute after:inset-0 after:-z-10 after:rounded-[inherit] after:bg-(--combobox-color)/10
      `,
      subtle: `
        relative isolate backdrop-blur-lg
        text-(--color-foreground) border-(--combobox-color)/35 hover:bg-(--combobox-color)/25

        before:absolute before:inset-0 before:-z-10 before:rounded-[inherit] before:bg-(--color-background)/50
        after:absolute after:inset-0 after:-z-10 after:rounded-[inherit] after:bg-(--combobox-color)/15
      `,
      ghost: `
        bg-transparent text-(--color-foreground)
        border-transparent
        hover:bg-(--combobox-color)/5
      `,
    },

    size: {
      xs: `px-2 h-6 text-xs rounded`,
      sm: `px-2.5 h-8 text-sm rounded-md`,
      md: `px-3 h-10 text-base rounded-lg`,
      lg: `px-4 h-12 text-lg rounded-lg`,
      xl: `px-5 h-14 text-xl rounded-lg`,
    },

    open: {
      true: `ring-2 ring-white`,
      false: ``,
    },

    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: `focus-within:ring-2`,
    },

    multiple: {
      true: `flex-wrap`,
      false: ``,
    },

    block: {
      true: `w-full`,
      false: ``,
    },
  },

  defaults: {
    color: "neutral",
    variant: "subtle",
    size: "md",
    open: "false",
    disabled: "false",
    multiple: "false",
    block: "false",
  },

  compoundVariants: [
    // Open state - rotate trigger indicator
    {
      open: "true",
      class: {
        trigger: `rotate-180`,
      },
    },
  ],
});

/**
 * Tailwind-like class variants for `<e-combobox-trigger>`.
 */
export const comboboxTriggerClasses = defineVariants({
  base: `
    inline-flex items-center justify-center
    p-1
    rounded
    cursor-pointer
    hover:bg-(--color-elevation-200)
    transition-colors
  `,

  variants: {
    disabled: {
      true: `opacity-50 cursor-not-allowed!`,
      false: ``,
    },
  },

  defaults: {
    disabled: "false",
  },
});

/**
 * Tailwind-like class variants for `<e-combobox-content>`.
 */
export const comboboxContentClasses = defineVariants(dropdownContentConfig);

/**
 * Tailwind-like class variants for `<e-combobox-item>`.
 * Note: Most styling comes from the underlying e-listbox-item.
 */
export const comboboxItemClasses = defineVariants({
  base: ``,

  variants: {
    disabled: {
      true: ``,
      false: ``,
    },
    filtered: {
      true: ``,
      false: `hidden`,
    },
  },

  defaults: {
    disabled: "false",
    filtered: "true",
  },
});

/**
 * Tailwind-like class variants for `<e-combobox-group>`.
 * Note: Most styling comes from the underlying e-listbox-group.
 */
export const comboboxGroupClasses = defineVariants(dropdownGroupConfig);

/**
 * Tailwind-like class variants for `<e-combobox-item-indicator>`.
 */
export const comboboxItemIndicatorClasses = defineVariants({
  base: `flex-shrink-0`,

  variants: {
    selected: {
      true: `opacity-100`,
      false: `opacity-0`,
    },
  },

  defaults: {
    selected: "false",
  },
});

/**
 * Tailwind-like class variants for `<e-combobox-empty>`.
 */
export const comboboxEmptyClasses = defineVariants({
  base: `
    px-3 py-2
    text-center
    text-(--color-foreground)/60
    text-sm
  `,

  variants: {},

  defaults: {},
});

/**
 * Tailwind-like class variants for `<e-combobox-separator>`.
 */
export const comboboxSeparatorClasses = defineVariants({
  base: `
    h-px
    my-1
    mx-2
    bg-(--color-elevation-200)
  `,

  variants: {},

  defaults: {},
});
